/* eslint-disable import/no-unresolved */
import * as yup from "yup";
import regex from "src/helpers/regex";
import {
  IJointLifeAddendum,
  JointExistingPolicyDetails,
  JointMedicalLifeStyle
} from "./interfaces";
import { validationSchema as generalDetailsValidationSchema } from "../ProposalDetails/GeneralDetails";
import { validationSchema as jointMedicalSchema } from "../ProposalDetails/MedicalAndLifeStyle";
import { addressValidationSchema as addressDetailsValidationSchema } from "../ProposalDetails/AddressDetails";
import { existingLifeCoverValidation } from "../LoanDetails";
import { IExistingLifeCover } from "../ProposalDetails/interfaces";

const joinExistingLifeCoverDetailsInitialValues: IExistingLifeCover = {
  acceptanceTerms: "",
  acceptanceTermsCode: "",
  nameOfInsuranceCompany: "",
  nameOfInsuranceCompanyCode: "",
  policyNumber: "",
  totalSumInsured: ""
};
const jointMemberInitialValues: IJointLifeAddendum = {
  member: {
    title: "",
    titleCode: "",
    otherTitle: "",
    gender: "",
    genderCode: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    maritalStatus: "",
    maritalStatusCode: "",
    nationality: "Indian",
    nationalityCode: "RES",
    countryOfResidence: "",
    cityOfResidence: "",
    dateOfLastEntry: "",
    educationQualification: "",
    educationQualificationCode: "",
    mobileNo: "",
    email: "",
    occupation: "",
    occupationCode: "",
    subOccupation: "",
    subOccupationCode: "",
    nameOfOrganization: "",
    designation: "",
    annualIncome: "",
    panNo: "",
    natureOfWork: "",
    relationship: "",
    relationshipCode: "",
    UIDNo: "",
    electronicAccount: "",
    eiaNumber: "",
    businessOwner: false,
    housewifeFlag: false
  },
  address: {
    address1: "",
    address2: "",
    address3: "",
    pinCode: "",
    state: "",
    city: ""
  },
  existingPolicy: {
    existingLifeCover: false,
    existingPolicyDetails: [joinExistingLifeCoverDetailsInitialValues]
  },
  isAddressProposer: false,
  jointCoBorrowerFlag: false,
  jointGuarantorFlag: false,
  jointHousewifeFlag: false,
  jointPrimaryBorrowerFlag: false
};

const jointMedicalInitialValues: JointMedicalLifeStyle = {
  medicalDetails: {
    declare: true,
    medicalHealth: false,
    weight: "",
    height: {
      centimeter: {
        cm: ""
      },
      inchFeet: {
        inches: "",
        feet: ""
      }
    },
    measurement: "cm",
    familyBackground: false,
    diabetes: false,
    highBloodPressure: false,
    stroke: false,
    asthma: false,
    epilepsy: false,
    liverDisease: false,
    kidneyDisease: false,
    bloodDisorder: false,
    tumor: false,
    anxiety: false,
    aids: false,
    smoker: false,
    cigaratte: false,
    bidi: false,
    cigar: false,
    panMasala: false,
    gutkha: false,
    chewing: false,
    cigaratteAmount: "",
    bidiAmount: "",
    cigarAmount: "",
    panMasalaAmount: "",
    gutkhaAmount: "",
    chewingAmount: "",
    alcohol: false,
    drugs: false,
    disability: false,
    gyneComplications: false,
    pregnant: false,
    pregnancyDuration: "",
    remarks: "",
    beer: false,
    wine: false,
    liquor: false,
    lessThanOnce: false,
    moreThanOnce: false,
    once: false,
    daily: false,
    alcoholAmount: "",
    familyHealthHistory: false,
    familyMedicalHistory: [
      {
        familyPersonName: "",
        familyRelation: "",
        status: "Alive",
        sufferingAge: "",
        ageOfDeath: "",
        presentStateOfHealth: "",
        causeDeath: ""
      }
    ],
    indigestion: false,
    thyroid: false,
    arthritis: false,
    diabetesAndSugar: false,
    bloodPressureHeartAttack: false,
    strokeParalysis: false,
    asthamaTuberculosis: false,
    kidneyStoneBloodUrine: false,
    anaemia: false,
    cancerTumor: false,
    anxietyDepression: false,
    otherDisease: false,
    occupationEnquiry: false,
    alcoholConsumptionSince: 0,
    smokingConsumptionSince: 0,
    existingApplication: false,
    familyHealth: false,
    hospitalisation: false,
    majorDisorder: false,
    underTreatment: false,
    heartDisease: false,
    lungDisease: false,
    highCholesterol: false,
    hivAidsSTI: false,
    congenitalDefect: false,
    genitourinaryHearingVisionDisorder: false,
    paralysis: false,
    brainNervousSystemDisorder: false,
    thyroidDisorder: false,
    chronicDigestiveDisorder: false,
    endocrineDisorder: false,
    anyOtherDisease: "",
    weightLossProgram: false,
    investigateCheckups: false,
    personalConsumptionSmoking: false,
    personalConsumptionTobacco: false,
    personalConsumptionAlcohol: false,
    isOccupationHazardious: false,
    mines: false,
    explosives: false,
    radiation: false,
    corrosiveChemicals: false,
    hobbyQuery: false,
    aviation: false,
    diving: false,
    mountaineering: false,
    seaDiving: false,
    motorRacing: false,
    bungeeJumping: false,
    familyHealthQuery: false,
    femaleOrientedQuery: false
  }
};

const jointMemberValidationSchema = (
  isPincodeInvalid: boolean,
  partnerId: any,
  formikJointHousewifeFlag: boolean,
  productId: any,
  memberDetailsSchema: any,
  salutationData: any,
  address: any
) =>
  yup.object().shape<Record<keyof IJointLifeAddendum, yup.AnySchema>>({
    member: generalDetailsValidationSchema(
      partnerId,

      productId,
      salutationData,
      memberDetailsSchema
    ).shape({
      relationship: yup.string().required("Relationship is Required"),
      annualIncome: yup
        .string()
        .when("formikJointHousewifeFlag", (_, schema) =>
          !formikJointHousewifeFlag
            ? schema
                .required("Annual Income is Required")
                .matches(regex.digits, "Invalid Annual Income")
                .max(10, "Max 10 digits allowed")
            : schema.notRequired()
        )
    }),
    address: addressDetailsValidationSchema(isPincodeInvalid, address),
    isAddressProposer: yup.boolean(),
    jointCoBorrowerFlag: yup.boolean(),
    jointGuarantorFlag: yup.boolean(),
    jointPrimaryBorrowerFlag: yup.boolean(),
    jointHousewifeFlag: yup.boolean(),
    existingPolicy: yup.object().shape<Record<keyof JointExistingPolicyDetails, yup.AnySchema>>({
      existingLifeCover: yup.boolean(),
      existingPolicyDetails: existingLifeCoverValidation(productId)
    }),
    medicalDetailsV2: yup.object().shape({})
  });

const jointMedicalValidationSchema = (productId: number, age: number, partnerId: string) =>
  yup.object().shape<Record<keyof JointMedicalLifeStyle, yup.AnySchema>>({
    medicalDetails: jointMedicalSchema(productId, age, partnerId)
  });

export {
  jointMemberInitialValues,
  joinExistingLifeCoverDetailsInitialValues,
  jointMedicalInitialValues,
  jointMemberValidationSchema,
  jointMedicalValidationSchema
};
